import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { challengesAPI, submissionsAPI, leaderboardAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { 
  CalendarIcon, 
  UserGroupIcon, 
  TrophyIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import { Button } from '../components/ui/button'
import { format } from 'date-fns'
import clsx from 'clsx'

const difficultyColors = {
  beginner: 'badge-success',
  intermediate: 'badge-warning',
  advanced: 'badge-error'
}

const statusColors = {
  active: 'badge-success',
  upcoming: 'badge-warning',
  completed: 'badge-secondary'
}

export default function ChallengeDetailPage() {
  const { id } = useParams()
  const { isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('overview')

  // Challenge details
  const { data: challengeData, isLoading: challengeLoading, error } = useQuery(
    ['challenge', id],
    () => challengesAPI.getById(id),
    {
      select: (response) => response.data.challenge
    }
  )

  // User's submission for this challenge
  const { data: userSubmission } = useQuery(
    ['user-submission', id, user?.id],
    () => submissionsAPI.getByUserAndChallenge(user.id, id),
    {
      enabled: !!user?.id && !!id,
      select: (response) => response.data[0] // Get first submission
    }
  )

  // Challenge leaderboard
  const { data: leaderboardData, isLoading: leaderboardLoading } = useQuery(
    ['challenge-leaderboard', id],
    () => leaderboardAPI.getByChallenge(id, { limit: 10 }),
    {
      select: (response) => response.data,
      enabled: Boolean(activeTab === 'leaderboard')
    }
  )

  // Challenge submissions
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery(
    ['challenge-submissions', id],
    () => submissionsAPI.getByChallenge(id, { limit: 20 }),
    {
      select: (response) => response.data,
      enabled: Boolean(activeTab === 'submissions')
    }
  )

  // Check if user has joined the challenge
  const { data: participationData } = useQuery(
    ['challenge-participation', id],
    () => challengesAPI.checkParticipation(id),
    {
      select: (response) => response.data,
      enabled: Boolean(isAuthenticated)
    }
  )

  // Join challenge mutation
  const joinChallengeMutation = useMutation(
    () => challengesAPI.join(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['challenge-participation', id])
      },
      onError: (error) => {
        console.error('Failed to join challenge:', error)
      }
    }
  )

  // Leave challenge mutation
  const leaveChallengeMutation = useMutation(
    () => challengesAPI.leave(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['challenge-participation', id])
      },
      onError: (error) => {
        console.error('Failed to leave challenge:', error)
      }
    }
  )

  if (challengeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !challengeData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Challenge not found</h2>
          <p className="text-gray-600 mb-4">The challenge you're looking for doesn't exist.</p>
          <Button asChild variant="gold">
            <Link to="/challenges">
              Back to Challenges
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  const isActive = challengeData.status === 'active'
  const hasSubmitted = !!userSubmission
  const hasJoined = participationData?.hasJoined || false
  const canJoin = isAuthenticated && isActive && !hasJoined
  const canSubmit = isAuthenticated && isActive && hasJoined && !hasSubmitted

  const tabs = [
    { id: 'overview', name: 'Overview' },
    { id: 'leaderboard', name: 'Leaderboard' },
    { id: 'submissions', name: 'Submissions' },
  ]

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center space-x-3">
              <span className="badge-primary">{challengeData.category}</span>
              <span className={`badge ${statusColors[challengeData.status]}`}>
                {challengeData.status}
              </span>
              <span className={`badge ${difficultyColors[challengeData.difficulty]}`}>
                {challengeData.difficulty}
              </span>
            </div>
          </div>
          
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                {challengeData.title}
              </h1>
              <div className="mt-2 flex items-center text-sm text-gray-500 space-x-6">
                <div className="flex items-center">
                  <UserGroupIcon className="h-4 w-4 mr-1" />
                  <span>{challengeData.participant_count} participants</span>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  <span>Ends {format(new Date(challengeData.end_date), 'MMM dd, yyyy')}</span>
                </div>
                {challengeData.prizes && challengeData.prizes.length > 0 && (
                  <div className="flex items-center">
                    <TrophyIcon className="h-4 w-4 mr-1" />
                    <span>Prizes available</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
              {canJoin && (
                <Button 
                  variant="gold" 
                  size="lg"
                  onClick={() => joinChallengeMutation.mutate()}
                  disabled={joinChallengeMutation.isLoading}
                >
                  {joinChallengeMutation.isLoading ? 'Joining...' : 'Join Challenge'}
                </Button>
              )}
              {hasJoined && !hasSubmitted && (
                <>
                  <Button asChild variant="gold" size="lg">
                    <Link to={`/submit/${challengeData.id}`}>
                      Submit Solution
                    </Link>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg"
                    onClick={() => leaveChallengeMutation.mutate()}
                    disabled={leaveChallengeMutation.isLoading}
                  >
                    {leaveChallengeMutation.isLoading ? 'Leaving...' : 'Leave Challenge'}
                  </Button>
                </>
              )}
              {hasSubmitted && (
                <div className="flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-md">
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Submitted
                </div>
              )}
              {!isAuthenticated && (
                <Button asChild variant="gold" size="lg">
                  <Link to="/login">
                    Login to Participate
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="sm:hidden">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.name}
                </option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={clsx(
                    'px-1 py-2 border-b-2 font-medium text-sm',
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{challengeData.description}</p>
                </div>
              </div>

              {/* Rules */}
              {challengeData.rules && challengeData.rules.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Rules</h3>
                  <ul className="space-y-2">
                    {challengeData.rules.map((rule, index) => (
                      <li key={index} className="flex items-start">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-medium mr-3 mt-0.5">
                          {index + 1}
                        </span>
                        <span className="text-gray-700">{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Evaluation Criteria */}
              {challengeData.evaluation_criteria && challengeData.evaluation_criteria.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Evaluation Criteria</h3>
                  <ul className="space-y-2">
                    {challengeData.evaluation_criteria.map((criteria, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircleIcon className="flex-shrink-0 w-5 h-5 text-green-500 mr-3 mt-0.5" />
                        <span className="text-gray-700">{criteria}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Challenge Info */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Challenge Info</h3>
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                    <dd className="text-sm text-gray-900">
                      {format(new Date(challengeData.start_date), 'MMM dd, yyyy')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">End Date</dt>
                    <dd className="text-sm text-gray-900">
                      {format(new Date(challengeData.end_date), 'MMM dd, yyyy')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Max Team Size</dt>
                    <dd className="text-sm text-gray-900">{challengeData.max_team_size}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Submissions</dt>
                    <dd className="text-sm text-gray-900">{challengeData.submission_count}</dd>
                  </div>
                </dl>
              </div>

              {/* Prizes */}
              {challengeData.prizes && challengeData.prizes.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Prizes</h3>
                  <ul className="space-y-2">
                    {challengeData.prizes.map((prize, index) => (
                      <li key={index} className="flex items-center">
                        <TrophyIcon className="flex-shrink-0 w-5 h-5 text-yellow-500 mr-3" />
                        <span className="text-gray-700">{prize}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* User's Submission Status */}
              {isAuthenticated && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Your Status</h3>
                  {hasSubmitted ? (
                    <div className="space-y-3">
                      <div className="flex items-center text-green-600">
                        <CheckCircleIcon className="w-5 h-5 mr-2" />
                        <span className="font-medium">Submitted</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p><strong>Title:</strong> {userSubmission.title}</p>
                        <p><strong>Status:</strong> {userSubmission.status.replace('_', ' ')}</p>
                        {userSubmission.score && (
                          <p><strong>Score:</strong> {userSubmission.score}</p>
                        )}
                      </div>
                      <Link
                        to={`/submissions`}
                        className="text-sm text-primary-600 hover:text-primary-500"
                      >
                        View submission details →
                      </Link>
                    </div>
                  ) : hasJoined && isActive ? (
                    <div className="space-y-3">
                      <div className="flex items-center text-blue-600">
                        <UserGroupIcon className="w-5 h-5 mr-2" />
                        <span className="font-medium">Joined - Ready to submit</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        You've joined this challenge. Submit your solution now!
                      </p>
                      <Button asChild variant="gold" size="lg" className="w-full">
                        <Link to={`/submit/${challengeData.id}`}>
                          Submit Solution
                        </Link>
                      </Button>
                    </div>
                  ) : canJoin ? (
                    <div className="space-y-3">
                      <div className="flex items-center text-yellow-600">
                        <ClockIcon className="w-5 h-5 mr-2" />
                        <span className="font-medium">Not joined</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Join this challenge to submit your solution.
                      </p>
                      <Button 
                        variant="gold" 
                        size="lg" 
                        className="w-full"
                        onClick={() => joinChallengeMutation.mutate()}
                        disabled={joinChallengeMutation.isLoading}
                      >
                        {joinChallengeMutation.isLoading ? 'Joining...' : 'Join Challenge'}
                      </Button>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <p>This challenge is no longer accepting submissions.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'leaderboard' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Challenge Leaderboard
              </h3>
              {leaderboardLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : leaderboardData?.leaderboard?.length > 0 ? (
                <div className="overflow-hidden">
                  <ul className="divide-y divide-gray-200">
                    {leaderboardData.leaderboard.map((entry) => (
                      <li key={entry.id} className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-8 text-center font-bold text-gray-700">
                              #{entry.rank}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {entry.username || entry.displayName || `user_${entry.id}`}
                              </div>
                              <div className="text-sm text-gray-500">
                                {entry.organization && `${entry.organization} • `}
                                {entry.country}
                              </div>
                              <div className="text-sm text-primary-600 font-medium">
                                {entry.title}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              {entry.score}
                            </div>
                            <div className="text-sm text-gray-500">
                              {format(new Date(entry.created_at), 'MMM dd')}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No rankings yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Be the first to submit a solution and appear on the leaderboard!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'submissions' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Recent Submissions
              </h3>
              {submissionsLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : submissionsData?.submissions?.length > 0 ? (
                <div className="space-y-4">
                  {submissionsData.submissions.map((submission) => (
                    <div key={submission.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">
                            {submission.title}
                          </h4>
                          <p className="text-sm text-gray-500">
                            by {submission.username || submission.displayName || `user_${submission.user_id}`}
                            {submission.organization && ` • ${submission.organization}`}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {format(new Date(submission.created_at), 'MMM dd, yyyy')}
                          </p>
                        </div>
                        <div className="text-right">
                          {submission.score && (
                            <div className="text-lg font-bold text-gray-900">
                              {submission.score}
                            </div>
                          )}
                          <div className="text-sm text-gray-500 capitalize">
                            {submission.status.replace('_', ' ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions yet</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Be the first to submit a solution for this challenge!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}